import api from './api';
import { House } from './types';

/**
 * Houses Service
 * Handles all house-related API requests
 */
const housesService = {
    /**
     * Get all houses
     * @returns Promise<House[]>
     */
    getAll: async (): Promise<House[]> => {
        try {
            const response = await api.get<{ houses: House[]; total: number; success: boolean }>('/houses');
            return response.houses || [];
        } catch (error) {
            console.error('housesService.getAll - Error fetching all houses:', error);
            throw error;
        }
    },

    /**
     * Get house by public ID
     * @param publicId - House public ID
     * @returns Promise<House>
     */
    getById: async (publicId: string): Promise<House> => {
        try {
            const response = await api.get<House>(`/houses/${publicId}`);
            return response;
        } catch (error) {
            console.error(`Error fetching house ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Get houses by user ID
     * @param userId - User public ID
     * @returns Promise<House[]>
     */
    getByUserId: async (userId: string): Promise<House[]> => {
        try {
            const response = await api.get<{ houses: House[]; total: number; success: boolean }>(`/houses/user/${userId}`);
            return response.houses || [];
        } catch (error) {
            console.error(`Error fetching houses for user ${userId}:`, error);
            throw error;
        }
    },

    /**
     * Create new house
     * @param houseData - House data
     * @returns Promise<House>
     */
    create: async (houseData: Partial<House>): Promise<House> => {
        try {
            const response = await api.post<House>('/houses', houseData);
            return response;
        } catch (error) {
            console.error('Error creating house:', error);
            throw error;
        }
    },

    /**
     * Update house
     * @param publicId - House public ID
     * @param houseData - House data to update
     * @returns Promise<House>
     */
    update: async (publicId: string, houseData: Partial<House>): Promise<House> => {
        try {
            const response = await api.put<House>(`/houses/${publicId}`, houseData);
            return response;
        } catch (error) {
            console.error(`Error updating house ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Delete house
     * @param publicId - House public ID
     * @returns Promise<void>
     */
    delete: async (publicId: string): Promise<void> => {
        try {
            await api.delete(`/houses/${publicId}`);
        } catch (error) {
            console.error(`Error deleting house ${publicId}:`, error);
            throw error;
        }
    }
};

export default housesService;